<template>
	<div class="content-sections">
		<section class="stack">
			<h4 class="sgnl-h6-semibold sgnl-text-color-default">Code Execution</h4>
			<div class="stack">
				<div
					data-test="default-core-selector"
					class="input-group"
				>
					<div class="field-label">Default execution core</div>
					<v-select
						item-title="Name"
						item-value="CoreID"
						:items="filteredCores"
						v-model="coresChosenDefaultCoreID"
						@update:model-value="setDefaultCore"
						:loading="coresStore.defaultCoreUpdateStatus.loading"
						attach
						hide-details
						:menu-icon="''"
					>
						<template #append-inner>
							<ChevronDown16 class="select-chevron" />
						</template>
					</v-select>
				</div>
				<div
					data-test="default-ref-core-selector"
					class="input-group"
				>
					<div class="field-label">Default reference core</div>
					<v-select
						item-title="Name"
						item-value="CoreID"
						:items="coresStore.filteredVisibleReferenceCoresList"
						v-model="coresChosenDefaultReferenceCoreID"
						@update:model-value="setDefaultReferenceCore"
						:loading="coresStore.defaultRefCoreUpdateStatus.loading"
						attach
						hide-details
						:menu-icon="''"
					>
						<template #append-inner>
							<ChevronDown16 class="select-chevron" />
						</template>
					</v-select>
				</div>
				<div class="input-group">
					<div class="field-label">
						Default Data Source Configuration
						<TooltipButton
							:icon="true"
							size="24"
							tooltipText="Reset to default"
							@click="resetDefaultMountConfig()"
						>
							<template #btnContent>
								<Reset16 />
							</template>
						</TooltipButton>
					</div>
					<section class="settings-field">
						<div
							v-if="currentEditorDefaultMountConfig"
							class="data-source-field"
						>
							<DataSourceInlineCard
								:key="currentEditorDefaultMountConfig.ResourceID"
								:dataSourceID="currentEditorDefaultMountConfig.ResourceID"
								:dataSourceType="currentEditorDefaultMountConfig.ResourceType"
								:mountLocation="currentEditorDefaultMountConfig.Location"
								hideDisconnectButton
							/>
							<TooltipButton
								:icon="true"
								variant="text"
								size="small"
								btnClass="sgnl-p-0"
								tooltipText="Remove Mount Point"
								@click="removeDefaultMountConfig()"
							>
								<template #btnContent>
									<DataError16 class="sgnl-icon-color-error" />
								</template>
							</TooltipButton>
						</div>
						<div
							v-else
							class="placeholder-content mount-source-dims"
						>
							<img
								src="@/assets/icons/data-sources/new-data-source.svg"
								width="48"
								height="48"
								alt="New Data Source"
							/>
							<div class="new-data-source-card-text sgnl-text-color-default">
								Add Default Source Mount Point
							</div>
							<v-btn
								variant="flat"
								color="primary"
								@click="openMountDataSourcesDialog('editor')"
							>
								<Add16 />
								<span>Add Source</span>
							</v-btn>
						</div>
					</section>

					<MountDataSourcesDialog
						v-if="mountDataSourcesDialogStatus.show"
						:showDialog="mountDataSourcesDialogStatus.show"
						@close-dialog="closeMountDataSourcesDialog"
						@set-data-sources="setDefaultMountConfig"
						@reset-data-sources="resetDefaultMountConfig"
					/>
				</div>
			</div>
		</section>

		<section class="stack">
			<h4 class="sgnl-h6-semibold sgnl-text-color-default">Editor</h4>
			<div class="stack">
				<div class="input-group">
					<div class="field-label">Theme</div>
					<div
						class="theme-row"
						data-test="editor-theme-selector"
					>
						<div class="theme-left">
							<v-select
								item-title="name"
								item-value="key"
								v-model="editorTheme"
								:items="editorThemeList"
								attach
								hide-details
								data-test="editor-theme-selector"
								:menu-icon="''"
							>
								<template #append-inner>
									<ChevronDown16 class="select-chevron" />
								</template>
							</v-select>
							<div class="input-group">
								<div class="field-label">Layout</div>
								<v-checkbox
									data-test="editor-variable-viewer-visibility-switch"
									label="Show variable viewer"
									v-model="editorVariableViewer"
									@update:model-value="toggleVariableViewerVisibility"
									hide-details
								/>
							</div>
						</div>
						<v-card
							variant="outlined"
							class="editor-placeholder-container sgnl-hidden sgnl-sm-block"
						>
							<EditorPlaceholder
								height="175px"
								:theme="editorTheme ?? `dawn`"
							/>
						</v-card>
					</div>
				</div>
				<v-fade-transition>
					<v-alert
						v-if="editorStore.themeUpdateStatus.error"
						density="compact"
						variant="outlined"
						type="warning"
						border="start"
						height="36px"
						class="sgnl-mt-0 sgnl-paragraph-s-regular sgnl-py-4"
					>
						Unable to save theme. {{ editorStore.themeUpdateStatus.message }} Please try again later.
					</v-alert>
				</v-fade-transition>
				<TooltipButton
					variant="flat"
					color="primary"
					:loading="editorStore.themeUpdateStatus.loading"
					btnText="Save"
					:disabled="editorTheme === editorStore.editorThemeID"
					@click="setEditorTheme(editorTheme ?? 'solarized-light')"
				>
					<template #btnContent>
						<Save16 />
						<span>Save</span>
					</template>
				</TooltipButton>
			</div>
		</section>
		<!-- <div class="form-row sgnl-flex-row-reverse sgnl-px-0 sgnl-px-md-16">
			<v-row justify="end">
				<v-col>
					<v-fade-transition>
						<v-alert
							v-if="saveError"
							density="compact"
							variant="outlined"
							type="warning"
							border="start"
							height="36px"
							class="sgnl-my-0 sgnl-paragraph-s-regular sgnl-py-4"
						>
							Unable to save settings. Please try again later.
						</v-alert>
					</v-fade-transition>
				</v-col>
				<v-col cols="auto">
					<TooltipButton
						color="primary"
						:loading="settingsSaveInProgress"
						btnText="Save"
						btnIcon=""
						@click="saveSettings"
					/>
				</v-col>
			</v-row>
		</div> -->
		<v-snackbar
			v-model="successSnackbar"
			:timeout="4000"
			class="snackbar-success"
		>
			<div class="snackbar-icon-container snackbar-success">
				<CheckmarkFilled16 />
			</div>
			{{ successMessage }}
			<template v-slot:actions>
				<v-btn
					variant="text"
					size="small"
					icon
					@click="successSnackbar = false"
				>
					<Close16 class="sgnl-icon-color-secondary" />
				</v-btn>
			</template>
		</v-snackbar>
		<v-snackbar
			v-model="errorSnackbar"
			:timeout="6000"
			class="snackbar-error"
		>
			<div class="snackbar-icon-container snackbar-error">
				<ErrorFilled16 />
			</div>
			{{ errorMessage }}
			<template v-slot:actions>
				<v-btn
					variant="text"
					size="small"
					icon
					@click="errorSnackbar = false"
				>
					<Close16 class="sgnl-icon-color-secondary" />
				</v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Reset16 from "@carbon/icons-vue/es/reset/16";
import DataError16 from "@carbon/icons-vue/es/data--error/16";
import Add16 from "@carbon/icons-vue/es/add/16";
import Save16 from "@carbon/icons-vue/es/save/16";
import ChevronDown16 from "@carbon/icons-vue/es/chevron--down/16";
import CheckmarkFilled16 from "@carbon/icons-vue/es/checkmark--filled/16";
import Close16 from "@carbon/icons-vue/es/close/16";
import ErrorFilled16 from "@carbon/icons-vue/es/error--filled/16";
import TooltipButton from "@/components/Common/TooltipButton.vue";
import { mockTimeout } from "@/js/githubClient";

import EditorPlaceholder from "@/components/Settings/EditorPlaceholder.vue";
import { sdk } from "@/js/sdk";
import MountDataSourcesDialog from "@/components/MountDataSourcesDialog.vue";
import DataSourceInlineCard from "@/components/DataSources/DataSourceInlineCard.vue";

import { mapState } from "pinia";
import { useUserStore } from "@/stores/user";
import { useCoresStore } from "@/stores/cores";
import { useEditorStore } from "@/stores/editor";
import { useRepositoriesStore } from "@/stores/repositories";
import { useDataSourcesStore } from "@/stores/dataSources";

import * as util from "@/js/utilities";
import { LocalMountConfig } from "@/types/api/dataSources";
import { Core } from "@/types/api/cores";
import { monitoringCaptureError } from "@/plugins/monitoring";

type ComponentData = {
	editorTheme: undefined | string;
	editorVariableViewer: undefined | boolean;
	editorLayout: undefined | string;
	coresChosenDefaultCoreID: undefined | string;
	coresChosenDefaultReferenceCoreID: undefined | string;
	settingsSaveInProgress: boolean;
	saveError: boolean;
	mountDataSourcesDialogStatus: {
		show: boolean;
		source?: any;
	};
	successSnackbar: boolean;
	successMessage: string;
	errorSnackbar: boolean;
	errorMessage: string;
};

export default defineComponent({
	name: "SettingsGeneral",
	components: {
		EditorPlaceholder,
		TooltipButton,
		MountDataSourcesDialog,
		DataSourceInlineCard,
		Reset16,
		DataError16,
		Add16,
		Save16,
		ChevronDown16,
		CheckmarkFilled16,
		Close16,
		ErrorFilled16,
	},
	data: (): ComponentData => ({
		editorTheme: undefined,
		editorVariableViewer: undefined,
		editorLayout: undefined,
		coresChosenDefaultCoreID: undefined,
		coresChosenDefaultReferenceCoreID: undefined,
		settingsSaveInProgress: false,
		saveError: false,
		mountDataSourcesDialogStatus: { show: false, source: null },
		successSnackbar: false,
		successMessage: "",
		errorSnackbar: false,
		errorMessage: "",
	}),
	setup() {
		const userStore = useUserStore();
		const coresStore = useCoresStore();
		const editorStore = useEditorStore();
		const dataSourcesStore = useDataSourcesStore();
		const repositoriesStore = useRepositoriesStore();
		return { userStore, coresStore, editorStore, repositoriesStore, dataSourcesStore };
	},
	computed: {
		...mapState(useEditorStore, {
			editorThemeList: "themeList",
		}),
		filteredCores(): Core[] {
			return this.coresStore.filteredVisibleNonReferenceCoresList.filter((c) => !c.Disabled && !c.Deprecated);
		},
		...mapState(useDataSourcesStore, {
			currentEditorDefaultMountConfig: "defaultMountConfig",
			currentRepositoriesDefaultMountConfig: "defaultMountConfig",
		}),
	},
	methods: {
		async saveSettings() {
			// * deprecated
			this.settingsSaveInProgress = true;
			this.editorStore.setEditorTheme(this.editorTheme, false);
			this.editorStore.setVariableViewerVisibility(this.editorVariableViewer ?? false, false);
			const defaultCoreIDToSet = this.coresChosenDefaultCoreID ?? this.coresStore.defaultCoreID;
			if (defaultCoreIDToSet !== undefined) {
				this.coresStore.setDefaultCore(defaultCoreIDToSet, true);
			}
			const defaultRefCoreIDToSet =
				this.coresChosenDefaultReferenceCoreID ?? this.coresStore.defaultReferenceCoreID;
			if (defaultRefCoreIDToSet !== undefined) {
				this.coresStore.setDefaultReferenceCore(defaultRefCoreIDToSet, false);
			}

			// * Try to save to the database
			try {
				const userID = await this.userStore.getCurrentUserID();

				if (!userID) {
					throw new Error("User ID not found");
				}
				const payload = {
					Editor_Theme: this.editorTheme,
					Editor_Layout_VariableViewer: String(this.editorVariableViewer),
					Execution_DefaultCore: this.coresChosenDefaultCoreID,
					Execution_DefaultReferenceCore: this.coresChosenDefaultReferenceCoreID,
				};
				await sdk.users.update(userID, { Preferences: payload });
				await this.userStore.fetchCurrentUserObjectFromDB();
			} catch (error) {
				monitoringCaptureError(error, "Save user settings");

				mockTimeout(5000).then(() => {
					this.saveError = false;
				});
			}
			this.settingsSaveInProgress = false;
		},
		openMountDataSourcesDialog(source) {
			this.mountDataSourcesDialogStatus.source = source;
			this.mountDataSourcesDialogStatus.show = true;
		},
		closeMountDataSourcesDialog() {
			this.mountDataSourcesDialogStatus.source = null;
			this.mountDataSourcesDialogStatus.show = false;
		},
		setDefaultMountConfig(mountConfig: LocalMountConfig) {
			this.dataSourcesStore.setDefaultMountConfig(mountConfig, true);
			this.closeMountDataSourcesDialog();
		},
		removeDefaultMountConfig() {
			this.dataSourcesStore.removeDefaultMountConfig();
		},
		resetDefaultMountConfig() {
			this.dataSourcesStore.resetDefaultMountConfig();
		},
		toggleVariableViewerVisibility() {
			this.editorStore.toggleVariableViewerVisibility(true);
		},
		setDefaultCore(coreID: string) {
			this.coresStore.setDefaultCore(coreID, true);
		},
		setDefaultReferenceCore(coreID: string) {
			this.coresStore.setDefaultReferenceCore(coreID, true);
		},
		async setEditorTheme(themeID: string) {
			await this.editorStore.setPreferredTheme(themeID);
			if (this.editorStore.themeUpdateStatus.error) {
				this.errorMessage = "Failed to save editor settings. Please try again.";
				this.errorSnackbar = true;
			} else {
				this.successMessage = "Editor settings saved successfully.";
				this.successSnackbar = true;
			}
		},
	},
	async beforeMount() {
		// Figure out how to gracefully get this information

		await this.userStore.getCurrentUserPreferences();

		util.waitFor(() => this.editorStore.editorThemeID !== undefined).then(() => {
			this.editorTheme = this.editorStore.editorThemeID;
		});

		util.waitFor(() => this.editorStore.editorThemeID !== undefined).then(() => {
			this.editorVariableViewer = this.editorStore.variableViewerVisibility;
		});

		util.waitFor(() => this.coresStore.defaultCoreID !== undefined).then(() => {
			this.coresChosenDefaultCoreID = this.coresStore.defaultCoreID;
		});

		util.waitFor(() => this.coresStore.defaultReferenceCoreID !== undefined).then(() => {
			this.coresChosenDefaultReferenceCoreID = this.coresStore.defaultReferenceCoreID;
		});
	},
});
</script>

<style lang="scss" scoped>
.settings-field {
	max-width: var(--sgnl-container-343);
}

.stack :deep(.v-select) {
	max-width: var(--sgnl-container-343);
}

.theme-row {
	display: flex;
	align-items: flex-start;
	gap: var(--sgnl-space-16);
}

.theme-left {
	width: var(--sgnl-container-343);
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-16);
}
@media (max-width: 599px) {
	.theme-left {
		width: 100%;
	}
}

.theme-left :deep(.v-select) {
	max-width: var(--sgnl-container-343);
}

.editor-placeholder-container {
	object-fit: cover;
	overflow: hidden;
	flex: 1;
	min-width: 0;
}

.data-source-field {
	display: flex;
	align-items: center;
	gap: 0.25rem;
}

.mount-source-dims {
	min-height: 140px;
	max-width: var(--sgnl-container-343);
}

.icon-secondary {
	color: var(--sgnl-default-secondary-icon);
}

.new-data-source-card-text {
	text-align: center;
	font-size: var(--sgnl-font-size-paragraph-s);
	font-weight: 400;
	line-height: var(--sgnl-font-line-height-paragraph-s);
}
</style>
